import { ChangeEvent, useEffect, useState, FocusEvent, Dispatch, useCallback } from 'react';
import { FieldArray } from 'react-final-form-arrays';
import { Link } from 'react-router-dom';
import debounce from 'lodash.debounce';
import { connect, useDispatch } from 'react-redux';
import { useUnmount } from 'react-use';

import { AutoComplete } from 'UIPackage/MainModule/components/AutoComplete';
import { IKnmIndex } from 'APIPackage/KnmModule/interfaces/IKnmIndex';
import { IKnmLink } from 'APIPackage/KnmModule/interfaces/IKnmLink';
import { DATE_FORMAT_LONG_FNS, SYSTEM_DATE_FORMAT_FNS } from 'CorePackage/MainModule/constants/dateFormats';
import { ROUTE_KNM, ROUTE_PM } from 'CorePackage/MainModule/routes/routes';
import { IKnmFormRef } from 'KnmPackage/MainModule/interfaces/IKnmFormRef';
import { getKnmFormRef, getKnmIsEditableInStatusCommentsExist } from 'KnmPackage/MainModule/selectors/knmSelectors';
import { Button } from 'UIPackage/MainModule/components/Button';
import { CloseButton } from 'UIPackage/MainModule/components/CloseButton';
import { ErknmFillInProcessMessage } from 'KnmErknmPackage/MainModule/components/ErknmFillInProcessMessage';
import { formatDate, formatDateLocale } from 'CorePackage/MainModule/common/formatDate';
import { TextInput } from 'UIPackage/MainModule/components/TextInput';
import { FieldBlock } from 'UIPackage/FormModule/components/FieldBlock';
import { IRootState } from 'CorePackage/MainModule/interfaces/IRootState';
import { RootActions } from 'CorePackage/MainModule/types/RootActions';
import { knmListFindKnm, knmListSetFindData } from 'KnmListPackage/MainModule/actions/knmListActions';
import { getKnmListFindData } from 'KnmListPackage/MainModule/selectors/knmListSelectors';

import styles from './ErknmLinks.module.scss';
import { LINKS_BLOCK_ID } from '../../../MainModule/constants/knmErknmFieldsElementsIds';
import { requiredRequirementsErknmOrApproveDocsOrLinks } from '../../../../CorePackage/MainModule/validators/requiredRequirementsErknmOrApproveDocsOrLinks';

const ERP_ID_MIN_LENGTH = 12;

interface IKnmAutocompleteOption {
  label: string;
  value: string;
  knm: IKnmIndex;
}

const generateKnmInfoText = (knm: IKnmIndex) =>
  `${knm.erpId ?? ''}${knm.knmType ?? ''} ${knm.organizationName ?? ''} ${knm.inn ? `(ИНН: ${knm.inn}` : ''}${
    knm.ogrn ? ` ОГРН: ${knm.ogrn}` : ''
  }) ${knm.controllingOrganization ?? ''}${knm.prosecutorOrganization ?? ''} ${formatDateLocale({
    date: knm.startDate,
    dateFormat: DATE_FORMAT_LONG_FNS,
  })} - ${knm.stopDate ? `${formatDateLocale({ date: knm.stopDate, dateFormat: DATE_FORMAT_LONG_FNS })}` : '?'}`;

const generateLinkInfoText = (knmLink: IKnmLink) =>
  `${knmLink.knmType ?? ''} ${knmLink.subjectName ?? ''} ${knmLink.inn ? `(ИНН: ${knmLink.inn})` : ''}${
    knmLink.ogrn ? ` (ОГРН: ${knmLink.ogrn})` : ''
  } ${knmLink.knoOrganizationName ?? ''} ${knmLink.prosecutorOfficeName ?? ''} ${formatDate({
    date: knmLink.startDate,
    dateFormat: DATE_FORMAT_LONG_FNS,
  })} - ${knmLink.stopDate ? `${formatDate({ date: knmLink.stopDate, dateFormat: DATE_FORMAT_LONG_FNS })}` : '?'}`;

interface IErknmLinksOldProps {
  watchOnly: boolean;
  isEnabled: boolean;
}

interface IErknmLinksOldStoreProps {
  formRef: IKnmFormRef | undefined;
  knms: IKnmIndex[];
  isAvailableInStatusCommentsExist: boolean;
}

const ErknmLinksOldWithoutStore = ({
  watchOnly,
  isEnabled,
  formRef,
  knms,
  isAvailableInStatusCommentsExist,
}: IErknmLinksOldProps & IErknmLinksOldStoreProps) => {
  const dispatch = useDispatch<Dispatch<RootActions>>();
  const push = formRef?.current.mutators.push;
  const remove = formRef?.current.mutators.remove;

  const [isAddingFieldsActive, setIsAddingFieldsActive] = useState(false);
  const [knmsAutocompleteOptions, setKnmsAutocompleteOptions] = useState<IKnmAutocompleteOption[] | undefined>();
  const [erpIdInputValue, setErpIdInputValue] = useState('');
  const [isErpIdSelected, setIsErpIdSelected] = useState(false);
  const [lastKnmsAutocompleteOptions, setLastKnmsAutocompleteOptions] = useState<IKnmAutocompleteOption[]>([]);
  const [isFounded, setIsFounded] = useState(false);

  const openLinkFields = useCallback(() => setIsAddingFieldsActive(true), []);
  const closeLinkFields = useCallback(() => setIsAddingFieldsActive(false), []);

  function findKnms(paramErpId?: string) {
    if (paramErpId && paramErpId.length >= ERP_ID_MIN_LENGTH) {
      setIsFounded(true);
      dispatch(
        knmListFindKnm(
          { erpId: paramErpId, isKnmLinkQuery: true, ignoreProsecutorOffice: true, ignoreKno: true },
          2000,
        ),
      );
    }
  }

  const debouncedGetKnms = debounce(findKnms, 3000);

  const handleTextInputChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const { value } = e.target;
      const valueEdited = value?.trim();

      setErpIdInputValue(valueEdited);
      if (!valueEdited) {
        setIsErpIdSelected(false);
      }
      debouncedGetKnms(valueEdited);
      if (!valueEdited || valueEdited.length <= ERP_ID_MIN_LENGTH) {
        setKnmsAutocompleteOptions(undefined);
      }
    },
    [debouncedGetKnms],
  );

  const handleAutoCompleteSelect = useCallback(
    (value: string) => () => {
      setErpIdInputValue(value);
      debouncedGetKnms('');
      setKnmsAutocompleteOptions(undefined);
      setIsErpIdSelected(true);
    },
    [debouncedGetKnms],
  );

  useEffect(() => {
    if (!isAddingFieldsActive) {
      setErpIdInputValue('');
      setIsErpIdSelected(false);
    }
  }, [isAddingFieldsActive, formRef]);

  useEffect(() => {
    isFounded &&
      setKnmsAutocompleteOptions(
        knms.map(knm => {
          const knmText = `${generateKnmInfoText(knm)}`;

          return {
            value: knmText,
            label: knmText,
            knm,
          };
        }),
      );
  }, [knms, isFounded]);

  useUnmount(() => {
    dispatch(knmListSetFindData([]));
  });

  useEffect(() => {
    knmsAutocompleteOptions && setLastKnmsAutocompleteOptions(knmsAutocompleteOptions);
  }, [knmsAutocompleteOptions]);

  const handleTextInputBlur = useCallback(
    (e: FocusEvent<HTMLInputElement>) => {
      const tagName = (e?.relatedTarget as Element)?.tagName;

      if (tagName !== 'UL') {
        debouncedGetKnms('');
        setKnmsAutocompleteOptions(undefined);
      }
    },
    [debouncedGetKnms],
  );

  const handleTextInputClear = useCallback(() => {
    setErpIdInputValue('');
    setIsErpIdSelected(false);
  }, []);

  const addLink = useCallback(
    (knm: IKnmIndex) => {
      if (push) {
        push('links', {
          erpId: knm.erpId,
          id: knm.id,
          knmType: knm.knmType,
          subjectName: knm.organizationName,
          inn: knm.inn,
          ogrn: knm.ogrn,
          knoOrganizationName: knm.controllingOrganization,
          prosecutorOfficeName: knm.prosecutorOrganization,
          startDate: formatDateLocale({ date: knm.startDate, dateFormat: SYSTEM_DATE_FORMAT_FNS }),
          stopDate: knm.stopDate ? formatDateLocale({ date: knm.stopDate, dateFormat: SYSTEM_DATE_FORMAT_FNS }) : null,
        });
      }
      setErpIdInputValue('');
      setIsErpIdSelected(false);
    },
    [push],
  );

  const deleteLink = (linkedErpId: string) => {
    const formLinks: IKnmLink[] | undefined = formRef?.current.getFieldState('links')?.value;
    const deletingLinkIndex = formLinks?.findIndex(formLink => formLink.erpId === linkedErpId);

    if (typeof deletingLinkIndex === 'number' && deletingLinkIndex >= 0 && remove) {
      remove('links', deletingLinkIndex);
    }
  };

  const handleSaveButtonClick = useCallback(() => {
    const knmAutocompleteOption = lastKnmsAutocompleteOptions?.find(
      lastKnmAutocompleteOption => lastKnmAutocompleteOption.value === erpIdInputValue,
    );
    const knm = knmAutocompleteOption?.knm;

    if (knm) {
      addLink(knm);
    }
  }, [addLink, erpIdInputValue, lastKnmsAutocompleteOptions]);

  const makeLinkDeleteButtonClickHandler = (linkedId: string) => () => {
    if (!watchOnly && linkedId && isEnabled) {
      deleteLink(linkedId);
    }
  };

  return (
    <FieldBlock
      label="Ссылка на другой паспорт КНМ или ПМ"
      id={LINKS_BLOCK_ID}
      button={
        <Button
          className="shared-btn-add"
          text="Добавить"
          type="button"
          variant="link"
          color="primary"
          disabled={watchOnly || isAddingFieldsActive || !isEnabled}
          onClick={openLinkFields}
        />
      }
      fillInProcess={<ErknmFillInProcessMessage name="links" />}
    >
      {isAddingFieldsActive && (
        <div>
          <div className={styles.LinkFields}>
            <AutoComplete
              type="area"
              options={knmsAutocompleteOptions}
              noFoundMessage="КНМ, соответствующие указанному учетному номеру, не найдены"
              handleAutoCompleteSelect={handleAutoCompleteSelect}
            >
              <TextInput
                id="linkErpId"
                name="linkErpId"
                placeholder="Введите учетный номер КНМ"
                clearable={isErpIdSelected}
                value={erpIdInputValue}
                onBlur={handleTextInputBlur}
                onChange={handleTextInputChange}
                onClear={handleTextInputClear}
              />
            </AutoComplete>
            <Button
              className={styles.SaveButton}
              text="Сохранить"
              color="default"
              size="medium"
              type="button"
              id="saveButton"
              disabled={watchOnly || !isErpIdSelected || !isEnabled}
              onClick={handleSaveButtonClick}
            />
            <Button
              className={styles.CancelButton}
              variant="link"
              color="primary"
              text="Отмена"
              size="medium"
              type="button"
              onClick={closeLinkFields}
            />
          </div>
        </div>
      )}

      <FieldArray
        name="links"
        validate={isAvailableInStatusCommentsExist ? requiredRequirementsErknmOrApproveDocsOrLinks : () => undefined}
        key={isAvailableInStatusCommentsExist ? 0 : 1}
      >
        {({ fields }) => (
          <ul className={styles.Links}>
            {fields.map((name, index) => {
              const isPm = fields.value?.[index]?.knmType === null;
              const linkId = fields.value?.[index]?.id;

              return (
                <li className={styles.Link} key={name}>
                  <Link className={styles.IdLink} to={isPm ? ROUTE_PM(linkId) : ROUTE_KNM(linkId)} target="blank">
                    {fields.value?.[index]?.erpId}
                  </Link>
                  <div className={styles.LinkText}>{generateLinkInfoText(fields.value?.[index])}</div>
                  <CloseButton
                    className={styles.LinkDeleteButton}
                    disabled={watchOnly || !isEnabled}
                    onClick={makeLinkDeleteButtonClickHandler(fields.value?.[index]?.erpId)}
                  />
                </li>
              );
            })}
          </ul>
        )}
      </FieldArray>
    </FieldBlock>
  );
};

const mapStateToProps = (state: IRootState): IErknmLinksOldStoreProps => ({
  formRef: getKnmFormRef(state),
  knms: getKnmListFindData(state),
  isAvailableInStatusCommentsExist: getKnmIsEditableInStatusCommentsExist(state),
});

export const ErknmLinksOld = connect(mapStateToProps)(ErknmLinksOldWithoutStore);
